import { ExternalLink, Trophy } from 'lucide-react';

import { cn } from '../../../lib/utils';
import {
  formatPrizeAmount,
  isValidExternalUrl,
  normalizePrizePool,
  normalizeSponsors,
} from './tournamentHelpers';

const TournamentSupportSection = ({ tournament }) => {
  const sponsors = normalizeSponsors(tournament?.sponsors);
  const prizePool = normalizePrizePool(tournament?.prizePool, tournament);
  const hasPrize = prizePool.status !== 'none';
  if (!hasPrize && sponsors.length === 0) return null;

  return (
    <section
      aria-label="Prize pool and sponsors"
      className="mx-auto max-w-screen-2xl px-4 pb-2 pt-8 sm:px-6 lg:pt-10"
    >
      <div
        className={cn(
          'grid gap-4 rounded-md border border-border/60 bg-background/40 p-4 sm:p-5',
          hasPrize && sponsors.length > 0 && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]'
        )}
      >
        {hasPrize && (
          <article className="min-w-0 border-b border-border/60 pb-4 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <Trophy className="h-3.5 w-3.5 text-heat" />
              Prize pool
            </div>
            <div className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              {prizePool.status === 'published'
                ? formatPrizeAmount(prizePool.amount, prizePool.currency) || 'Amount pending'
                : 'Amount undisclosed'}
            </div>
            {prizePool.contributors.length > 0 && (
              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Contributed by
                </div>
                <ul className="mt-2 grid gap-1.5 text-sm sm:grid-cols-2">
                  {prizePool.contributors.map((contributor, index) => (
                    <li
                      key={`${contributor.name || 'contributor'}-${index}`}
                      className="flex min-w-0 justify-between gap-3"
                    >
                      <span className="truncate text-muted-foreground">
                        {contributor.name || 'Community contributor'}
                      </span>
                      {prizePool.status === 'published' && contributor.amount !== '' && (
                        <span className="shrink-0 font-mono text-xs tabular-nums">
                          {formatPrizeAmount(
                            contributor.amount,
                            contributor.currency || prizePool.currency
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        )}

        {sponsors.length > 0 && (
          <article className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Sponsors &amp; partners
            </div>
            <div className="mt-3 grid grid-cols-2 items-stretch gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {sponsors.map((sponsor, index) => {
                const sponsorLink = isValidExternalUrl(sponsor.link) ? sponsor.link : '';
                const content = (
                  <span className="flex min-h-20 items-center justify-center gap-2 rounded-md border border-border/60 bg-background/50 p-3 text-center transition-colors hover:border-foreground/40">
                    {sponsor.logoUrl && (
                      <img
                        src={sponsor.logoUrl}
                        alt={sponsor.name || `Sponsor ${index + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="h-14 w-auto min-w-0 max-w-[60%] object-contain"
                      />
                    )}
                    {!sponsor.logoUrl && sponsor.name && (
                      <span className="text-sm font-medium">{sponsor.name}</span>
                    )}
                  </span>
                );
                return sponsorLink ? (
                  <a
                    key={`${sponsor.name || sponsor.logoUrl || 'sponsor'}-${index}`}
                    href={sponsorLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group"
                  >
                    {content}
                    <span className="sr-only">Open sponsor link</span>
                    <ExternalLink className="sr-only" />
                  </a>
                ) : (
                  <div key={`${sponsor.name || sponsor.logoUrl || 'sponsor'}-${index}`}>
                    {content}
                  </div>
                );
              })}
            </div>
          </article>
        )}
      </div>
    </section>
  );
};

export default TournamentSupportSection;
